<script setup lang="ts">
import ServerCard from '@/components/ServerCard.vue'
import { useMagicKeys, whenever } from '@vueuse/core'
import { useAllStores } from '@/store'
import { useIntervalFn } from '@vueuse/core'
import { useMyFetch } from '@/utils'
import { computed, ref } from 'vue'
import { handleError } from '@/utils/errors'

const { organizationStore, alertStore, authStore, router, t } = useAllStores()
const servers = ref<any[]>([])

async function fetchServers() {
  const { data, error } = await useMyFetch(`servers/${organizationStore.organization.id}`)
    .get()
    .json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
    if (error.value.code === 404) {
      // no such org is present
      organizationStore.clearOrganization()
      await router.push({ name: 'organizations:list' })
    }
  } else {
    servers.value = data.value
  }
}
await fetchServers()

const { pause, resume, isActive } = useIntervalFn(async () => {
  await fetchServers()
}, 5000)

const filtered = computed(() => {
  if (!servers.value) return []
  const filter = filterBy.value.toLowerCase()
  return servers.value.filter((server: any) => {
    return (
      server.name?.toLowerCase().includes(filter) ||
      server.ip?.toLowerCase().includes(filter) ||
      server.tags?.some((tag: string) => tag.toLowerCase().includes(filter))
    )
  })
})

function hasNoServers() {
  return !servers.value || servers.value.length === 0
}

const search = ref<HTMLElement>()
const filterBy = ref<string>('')

whenever(useMagicKeys().meta_k, () => {
  search.value?.focus()
})
</script>

<i18n lang="yaml">
en:
  add: Add server
  not_found: 'No servers found. Register one:'
  search_help: 'Search by name, IP or tag (⌘K)'
ru:
  add: Добавить сервер
  not_found: 'Серверы не найдены. Зарегистрируйте новый:'
  search_help: 'Поиск по имени, IP или тегу (⌘K)'
</i18n>

<template>
  <div class="row mb-2">
    <div class="col">
      <h1 v-if="hasNoServers()" class="mb-2">{{ t('not_found') }}</h1>

      <router-link
        class="btn btn-outline-primary mt-2"
        :class="{ 'btn-lg': hasNoServers() }"
        :to="{ name: 'servers:add' }"
      >
        <img src="@/assets/plus.svg" />{{ t('add') }}
      </router-link>
    </div>
    <div class="col" v-if="!hasNoServers()">
      <div class="input-group mt-2">
        <div class="input-group-text"><img src="@/assets/search16.svg" /></div>
        <input
          ref="search"
          v-model="filterBy"
          type="text"
          class="form-control"
          id="inlineFormInputGroupUsername"
          :placeholder="t('search_help')"
        />
      </div>
    </div>
  </div>
  <div class="row row-cols-1 row-cols-md-2 row-cols-xl-4 g-4">
    <div class="col" v-for="server in filtered" :key="server.id">
      <ServerCard
        :id="server.id"
        :title="server.name"
        :ip="server.ip"
        :description="server.description"
        :tags="server.tags"
        :user_count="server.user_count"
        :online="server.online"
        :user="server.current_user"
      />
    </div>
  </div>
</template>
